import {
  ConfirmedResellerIcon,
  InfluencerIcon,
  IntermediateResellerIcon
} from "assets/icons";
import { If } from "components";
import { t } from "i18next";
import { CommissionLabel, Icon } from "ui";

import {
  getProductCommissionValue,
  getRoundedDiscountedProductPrice
} from "../../../../helpers";
import { ProductBadge } from "../../../ProductBadge";
import { NameAndPricesBlockProps } from "./NameAndPricesBlock.types";

import styles from "./NameAndPricesBlock.module.scss";

const NameAndPricesBlock = ({
  activeProductVariant
}: NameAndPricesBlockProps) => {
  const {
    name,
    standardPrice,
    intermediateResellerPrice,
    confirmedResellerPrice,
    influencerPrice,
    discount,
    discountAmount,
    commission,
    commissionPercentage,
    influencerCommissionPercentage
  } = activeProductVariant;

  const pricesMap = [
    {
      title: t("Standard Price"),
      icon: null,
      discountedPrice: getRoundedDiscountedProductPrice(
        standardPrice,
        discount,
        discountAmount
      ),
      fullPrice: standardPrice,
      commission: getProductCommissionValue(
        getRoundedDiscountedProductPrice(
          standardPrice,
          discount,
          discountAmount
        ),
        commission,
        commissionPercentage
      )
    },
    {
      title: t("Intermediate Reseller Price"),
      icon: IntermediateResellerIcon,
      discountedPrice: getRoundedDiscountedProductPrice(
        intermediateResellerPrice,
        discount,
        discountAmount
      ),
      fullPrice: intermediateResellerPrice,
      commission: getProductCommissionValue(
        getRoundedDiscountedProductPrice(
          intermediateResellerPrice,
          discount,
          discountAmount
        ),
        commission,
        commissionPercentage
      )
    },
    {
      title: t("Confirmed Reseller Price"),
      icon: ConfirmedResellerIcon,
      discountedPrice: getRoundedDiscountedProductPrice(
        confirmedResellerPrice,
        discount,
        discountAmount
      ),
      fullPrice: confirmedResellerPrice,
      commission: getProductCommissionValue(
        getRoundedDiscountedProductPrice(
          confirmedResellerPrice,
          discount,
          discountAmount
        ),
        commission,
        commissionPercentage
      )
    },
    {
      title: t("Influencer Price"),
      icon: InfluencerIcon,
      discountedPrice: getRoundedDiscountedProductPrice(
        influencerPrice,
        discount,
        discountAmount
      ),
      fullPrice: influencerPrice,
      commission: getProductCommissionValue(
        getRoundedDiscountedProductPrice(
          influencerPrice,
          discount,
          discountAmount
        ),
        // Influencers are paid a percentage of the paid price only — the flat
        // column is ignored by the backend.
        undefined,
        influencerCommissionPercentage
      )
    }
  ];

  return (
    <div className={styles.nameAndPrices} data-testid="productInfo--namePrice">
      <div className={styles.nameAndPrices_name}>{name}</div>

      {activeProductVariant.badge && (
        <ProductBadge badge={activeProductVariant.badge} />
      )}

      <div className={styles.nameAndPrices_pricesGrid}>
        {pricesMap.map((price) => (
          <div key={price.title}>
            <div className={styles.nameAndPrices_priceTitle}>
              {price.icon && <Icon as={price.icon} boxSize={20} />}
              {price.title}:
            </div>

            <div className={styles.nameAndPrices_priceContainer}>
              <div className={styles.nameAndPrices_actualPrice}>
                {`${price.discountedPrice} FCFA`}
              </div>

              <If condition={price.fullPrice !== price.discountedPrice}>
                <div
                  className={styles.nameAndPrices_fullPrice}
                >{`${price.fullPrice} FCFA`}</div>
              </If>
            </div>

            <If condition={!commission && price.commission}>
              <div className={styles.nameAndPrices_percentageCommission}>
                <CommissionLabel commissionValue={price.commission} />
              </div>
            </If>
          </div>
        ))}
      </div>

      <If condition={commission}>
        <div className={styles.nameAndPrices_fixedCommission}>
          <CommissionLabel
            commissionValue={commission}
            iconSize={20}
            fontSize={16}
            fixed
          />
        </div>
      </If>
    </div>
  );
};

export default NameAndPricesBlock;
